import {
  ApiReturnCampaign,
  FixedTimeRangeConfig,
} from '@highimpact-frontend/api-types';
import dayjs from 'dayjs';

/**
 * return the campaigns that should be selected by default (matching by name).
 */
export function getDefaultCampaignsFromFixedConfigs(
  defaults: FixedTimeRangeConfig[] | undefined,
  campaigns: ApiReturnCampaign[]
): ApiReturnCampaign[] {
  if (!defaults || defaults.length === 0) return [];
  const ids = new Set(
    defaults
      .flatMap((d) => d.campaign_ids)
      .filter((id) => id !== undefined && id !== null)
  );
  return campaigns.filter((c) => ids.has(c.id));
}

/**
 * Builds an array of FixedTimeRange objects from a list of campaigns.
 *
 * If noDivision is true, returns a single FixedTimeRange that spans from
 * the earliest start_date to the latest end_date across all campaigns.
 *
 * @param campaigns  Array of Campaigns to convert
 * @param noDivision Optional flag to disable splitting; false by default
 * @returns Array of FixedTimeRangeConfig
 */
export function buildFixedTimeRanges(
  campaigns: ApiReturnCampaign[],
  noDivision = false
): FixedTimeRangeConfig[] {
  if (!Array.isArray(campaigns) || campaigns.length === 0) return [];

  if (noDivision) {
    let minStart: string | undefined;
    let maxEnd: string | undefined;

    for (const { start_date, end_date } of campaigns) {
      if (start_date) {
        if (!minStart || dayjs(start_date).isBefore(dayjs(minStart))) {
          minStart = start_date;
        }
      }
      if (end_date) {
        if (!maxEnd || dayjs(end_date).isAfter(dayjs(maxEnd))) {
          maxEnd = end_date;
        }
      }
    }

    if (!minStart || !maxEnd) return [];

    return [
      {
        type: 'fixed_time_range',
        name: buildFixedRangeName(new Date(minStart), new Date(maxEnd)),
        campaign_ids: campaigns.map(({ id }) => id),
        startdate: minStart,
        enddate: maxEnd,
      },
    ];
  }

  return campaigns.map(({ name, start_date, end_date, id }) => ({
    type: 'fixed_time_range',
    name,
    campaign_ids: [id],
    startdate: start_date,
    enddate: end_date,
  }));
}

type IntlFormatters = {
  monthFormatter?: Intl.DateTimeFormat;
  yearFormatter?: Intl.DateTimeFormat;
  dayFormatter?: Intl.DateTimeFormat;
  monthYearFormatter?: Intl.DateTimeFormat;
};

/**
 * Format a single date using Intl while preserving locale-specific ordering.
 * Uses formatRange when available for two-date ranges; otherwise composes safely
 * from parts so we don't hard-code day-month-year order or ordinal suffixes.
 */
function formatSingleDate(date: Date, fmt?: Intl.DateTimeFormat) {
  const d =
    fmt ??
    new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  return d.format(date);
}

function formatRangeFallback(a: Date, b: Date, partsFmt: Intl.DateTimeFormat) {
  // Compose using formatToParts to preserve ordering and separators without ordinals.
  const aParts = partsFmt.formatToParts(a);
  const bParts = partsFmt.formatToParts(b);

  // Build strings by joining the same kind of parts (e.g., month + literal + day + literal + year)
  const joinParts = (parts: Intl.DateTimeFormatPart[]) =>
    parts.map((p) => p.value).join('');
  const aStr = joinParts(aParts);
  const bStr = joinParts(bParts);

  return `${aStr} - ${bStr}`;
}

/**
 * Build a human-friendly name for a fixed time range.
 */
export function buildFixedRangeName(
  startDate: Date,
  endDate: Date,
  formatters: IntlFormatters = {}
): string {
  const monthFormatter =
    formatters.monthFormatter ??
    new Intl.DateTimeFormat(undefined, { month: 'short' });
  const yearFormatter =
    formatters.yearFormatter ??
    new Intl.DateTimeFormat(undefined, { year: 'numeric' });
  const dayFormatter =
    formatters.dayFormatter ??
    new Intl.DateTimeFormat(undefined, { day: 'numeric' });
  const monthYearFormatter =
    formatters.monthYearFormatter ??
    new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric' });

  const start = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).startOf('day');

  // Single day: localized full date
  if (start.isSame(end)) {
    return formatSingleDate(start.toDate());
  }

  // Same month & year and contiguous days: "Oct 25-27, 2025" but localized
  if (start.year() === end.year() && start.month() === end.month()) {
    // Try to produce localized "Month dayStart - dayEnd, Year"
    // Use a parts formatter that includes month, day and year in locale order
    const partsFmt = new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    // If runtime supports formatRange, prefer that for correct locale-aware range formatting
    const supportsRange = typeof (partsFmt as any).formatRange === 'function';
    if (supportsRange) {
      // formatRange may produce something like "Oct 25–27, 2025" in locales that support it
      try {
        return (partsFmt as any).formatRange(start.toDate(), end.toDate());
      } catch {
        // fall through to fallback
      }
    }

    // Fallback: build "Month dayStart - dayEnd, Year" using parts
    const month = monthFormatter.format(start.toDate());
    const year = yearFormatter.format(start.toDate());
    const startDay = dayFormatter.format(start.toDate());
    const endDay = dayFormatter.format(end.toDate());
    return `${month} ${startDay}-${endDay}, ${year}`;
  }

  // Full single month (start 1st and end last day of same month): "Oct 2025" localized
  const startIsFirstOfMonth = start.date() === 1;
  const endIsLastOfMonth = end.isSame(end.endOf('month'), 'day');
  if (
    start.year() === end.year() &&
    start.month() === end.month() &&
    startIsFirstOfMonth &&
    endIsLastOfMonth
  ) {
    return monthYearFormatter.format(start.toDate());
  }

  // Full contiguous months within same year: "Oct - Dec, 2025" localized if possible
  if (
    startIsFirstOfMonth &&
    endIsLastOfMonth &&
    end.isAfter(start, 'month') &&
    start.year() === end.year()
  ) {
    // Use month formatter for each month and append year localized
    const startMonth = monthFormatter.format(start.toDate());
    const endMonth = monthFormatter.format(end.toDate());
    const year = yearFormatter.format(start.toDate());
    return `${startMonth} - ${endMonth}, ${year}`;
  }

  // Full year(s): Jan 1 -> Dec 31 -> "2025" or "2023 - 2025"
  const startIsJan1 = start.month() === 0 && start.date() === 1;
  const endIsDec31 = end.month() === 11 && end.date() === 31;
  if (startIsJan1 && endIsDec31) {
    if (start.year() === end.year()) {
      return yearFormatter.format(start.toDate());
    }
    return `${yearFormatter.format(start.toDate())} - ${yearFormatter.format(
      end.toDate()
    )}`;
  }

  // Week detection: start Monday and end Sunday and same ISO week/year -> "week 43 (Oct.), 2025"
  const startIsMonday = (start.day() + 6) % 7 === 0;
  const endIsSunday = end.day() === 0;
  if (startIsMonday && endIsSunday) {
    // compute ISO week number in a locale-neutral way
    const thursday = start.add(3 - ((start.day() + 6) % 7), 'day');
    const yearOfWeek = thursday.year();
    const weekStart =
      Math.floor(
        thursday.diff(dayjs(new Date(yearOfWeek, 0, 4)).startOf('day'), 'day') /
          7
      ) + 1;
    if (start.year() === end.year() && weekStart) {
      const month = monthFormatter.format(start.toDate());
      const year = yearFormatter.format(start.toDate());
      return `week ${weekStart} (${month}), ${year}`;
    }
  }

  // Generic localized range: try Intl.formatRange with a formatter that includes month/day/year
  const partsFmtRange = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  if (typeof (partsFmtRange as any).formatRange === 'function') {
    try {
      return (partsFmtRange as any).formatRange(start.toDate(), end.toDate());
    } catch {
      // continue to fallback
    }
  }

  // Fallback localized composition preserving locale ordering using formatToParts
  const fallbackPartsFmt = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return formatRangeFallback(start.toDate(), end.toDate(), fallbackPartsFmt);
}
